import { Component } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ManagementArea } from 'src/app/data-models/area-models/management-area.model';
import { LandingSite } from 'src/app/data-models/landing-site.model';
import { DailyCatchReport } from 'src/app/data-models/report-models/daily-catch-report.model';
import { PauseReport } from 'src/app/data-models/report-models/pause-report.model';
import { SubmissionService } from 'src/app/services/reporting/submission.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { EndReport } from '../../data-models/report-models/end-report.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { AuthenticationService } from '../../services/authentication.service';
import { Subarea } from 'src/app/data-models/area-models/subarea.model';
import { environment } from '../../../environments/environment';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { cloneDeep } from 'lodash';
import { EmailService } from 'src/app/services/reporting/email.service';
import { FieldChange, ReportDiffService } from 'src/app/services/reporting/report-diff.service';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';



@Component({
    selector: 'app-end-report',
    templateUrl: './end-report.component.html',
    styleUrls: ['./end-report.component.css'],
    standalone: false
})
export class EndReportComponent {

  isProduction: boolean = environment.production;

  isEditing = false;

  private originalEndReport!: EndReport;

  isDataLoaded: boolean = false; // flag that tells us if the HTML is ready to be rendered (when the page is first loaded)
  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  // data preambles
  userDetails: UserData = new UserData();
  currTrip: Trip = new Trip();
  relevantLandingSites: LandingSite[] = [];
  managementAreas: ManagementArea[] = []; // management areas selectable for the current trip's license area
  subAreas: Subarea[] = []; // subareas selectable for the chosen management area
  endFishingReport: EndReport = new EndReport();
  endDate: Date = new Date();
  minEndDate: Date | null = null; // earliest selectable end date — the last catch/pause report day
  endTime!: { hour: number; minute: number; };
  endManagementArea: ManagementArea = new ManagementArea();
  endSubArea: Subarea = new Subarea();
  reportStatus: string = '';
  isComplete: boolean = false;
  reportFailed: boolean = false;
  needsArrivalForm: boolean = false;
  tripHasMissingReports: boolean = false; // flag to indicate if the trip has missing reports (catch or pause reports)


  // end report form (used for all trips)
  endForm: UntypedFormGroup = new UntypedFormGroup({
    comments: new FormControl<string>('') // conditionally validated
  });
  
  arrivalForm: UntypedFormGroup = new UntypedFormGroup(
    {
      site: new FormControl<string>('', [Validators.required])
    },
    { validators: this.offloadAfterEndValidator() }
  );
  nonListedSite: boolean = false;
  plannedOffloadDate: Date = new Date();
  plannedOffloadTime!: { hour: number, minute: number };
  

  // page to page
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;

  constructor(
    private router: Router,
    public route: ActivatedRoute,
    private submissionService: SubmissionService,
    private authenticationService: AuthenticationService,
    public sharedMethodsService: SharedMethodsService,
    public commonDataService: CommonDataService,
    private onlineStatusService: OnlineStatusService,
    private emailService: EmailService,
    private dialog: MatDialog,
  ) {

    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['landing_sites', 'management_areas_list', 'subareas_list'];
    this.userDetails = UserStorageService.currUser;
    this.reportStatus = this.route.snapshot.queryParams.status;
    const nav = this.router.getCurrentNavigation()?.extras.state as any;

    if (this.reportStatus === 'post_submission_edit' && nav?.trip && nav?.report) {
      this.isEditing = true;
      this.currTrip = nav.trip as Trip;
      this.endFishingReport = nav.report as EndReport;

      // snapshot original for diffing later
      this.originalEndReport = cloneDeep(this.endFishingReport);

      commonDataService.getSpecifiedDocs(this.userDetails.userEmail, ['landing_sites', 'management_areas_list', 'subareas_list']).then(() => this.initialize());
      return;
    }
    
    this.authenticationService.getUserDetails().then(() => {
      UserStorageService.getCurrTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.currTrip = UserStorageService.currTrip;
        this.endFishingReport = this.currTrip.endFishingReport;
        commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => {
          this.initialize();
        });
      });
    });

  }


  /*  ---------- Report Initialization Methods  ---------- */


  // initialize values
  initialize(): void {
    
    this.plannedOffloadDate = this.sharedMethodsService.getCalendarDate(this.currTrip, this.plannedOffloadDate);
    this.relevantLandingSites = this.sharedMethodsService.getLandingSites(this.currTrip.licenseArea.licenseName, CommonDataService.landingSites);
    this.managementAreas = this.sharedMethodsService.getManagementAreas(this.currTrip.licenseArea.licenseName, CommonDataService.managementAreas);
    this.setDateAndArea();
    this.minEndDate = this.getLatestReportDay(); // end date can't precede any reported fishing activity
    this.subAreas = this.sharedMethodsService.getSubareas(this.endManagementArea.name, false, CommonDataService.subareas);
    this.checkTripHasMissingReports(this.endDate);
    this.configureArrivalForm();

    if (this.reportStatus == 'complete') {

      if (SharedMethodsService.getReportStatus(this.endFishingReport.confirmationNumber) == 'Error with submission') {
        this.reportFailed = true;
      }
      this.isComplete = true;
      this.patchForm();
      this.currPage = 4;
      this.spinnerProgress = 100;
      
    } else if (this.isEditing) {
      this.patchForm();
      this.toNextPage();
    } else {
      this.toNextPage();
    }

    // Apply the custom validator to require comments when site = 'Not Listed'
    this.arrivalForm.get('site')?.valueChanges.subscribe(value => {
      this.updateCommentsValidator(value);
    });

    this.arrivalForm.updateValueAndValidity();

    this.isDataLoaded = true; 

  }


  patchForm() {

    this.endDate = this.endFishingReport.endDate;
    this.endTime = { hour: this.endDate.getHours(), minute: this.endDate.getMinutes() }
    this.endManagementArea = this.endFishingReport.managementArea;
    this.endSubArea = this.endFishingReport.subArea;
    this.plannedOffloadDate = this.endFishingReport.plannedOffloadDate;
    this.plannedOffloadTime = { hour: this.plannedOffloadDate.getHours(), minute: this.plannedOffloadDate.getMinutes() }

    this.endForm.patchValue({
      comments: this.endFishingReport.comments,
    });

    this.arrivalForm.patchValue({
      site: this.endFishingReport.landingSite,
    });

  }

  // Reopens the report, only possible if a submitted report gets a failure response from FOS
  reopenReport() {

    this.currPage = 1;
    this.spinnerProgress = this.spinnerIncrement;
    this.isComplete = false;
    this.reportFailed = false;

  }


  // gets the last submitted report date and management area (will either be from a catch or pause report)
  setDateAndArea(): void {

    let catchReports: DailyCatchReport[] = this.currTrip.dailyCatchReports.filter((cr) => cr.isComplete);
    let pauseReports: PauseReport[] = this.currTrip.pauseFishingReports.filter((pr) => pr.isComplete);
    let lastCatch = catchReports.length ? catchReports.reduce((curr, acc) => curr.dateFished.toISOString() > acc.dateFished.toISOString() ? curr : acc) : null;
    let lastPause = pauseReports.length ? pauseReports.reduce((curr, acc) => curr.pauseDate.toISOString() > acc.pauseDate.toISOString() ? curr : acc) : null;

    if (lastCatch && lastPause) {
      if (lastCatch.dateFished.toISOString() > lastPause.pauseDate.toISOString()) {
        this.endDate = lastCatch.dateFished;
        this.endManagementArea = lastCatch.managementArea;
        let subAreas = lastCatch.subAreas.filter((area: Subarea) => !area.zone);
        this.endSubArea = subAreas.length == 0 ? new Subarea() : subAreas[subAreas.length - 1];
      } else {
        this.endDate = lastPause.pauseDate;
        this.endManagementArea = lastPause.managementArea;
      }
    } else if (lastCatch) {
      this.endDate = lastCatch.dateFished;
      this.endManagementArea = lastCatch.managementArea;
      let subAreas = lastCatch.subAreas.filter((area: Subarea) => !area.zone);
      this.endSubArea = subAreas.length == 0 ? new Subarea() : subAreas[subAreas.length - 1];
    } else if (lastPause) {
      this.endDate = lastPause.pauseDate;
      this.endManagementArea = lastPause.managementArea;
    } else {
      this.endDate = new Date();
    }
  }

  // whether license/area combination requires the arrival form (page 2), and the resulting page count.
  // Re-run whenever the management area changes, since that decides needsArrivalForm for license A/C.
  configureArrivalForm(): void {
    this.needsArrivalForm = this.checkLicenseArea();

    if (this.needsArrivalForm) {
      this.arrivalForm.get('site')?.setValidators([Validators.required]);
      this.numPages = 3;
    } else {
      this.arrivalForm.get('site')?.clearValidators();
      this.numPages = 2;
    }
    this.arrivalForm.get('site')?.updateValueAndValidity();
    this.spinnerIncrement = 100 / this.numPages;
  }

  checkLicenseArea(): boolean {
    if (this.currTrip.licenseArea.licenseName === "G") return true;

    if (["A", "C"].includes(this.currTrip.licenseArea.licenseName)) {
      if (["3", "4", "5"].includes(this.endManagementArea.name)) {
        return true;
      }
    }
    return false;
  }


  compareSite(a1: LandingSite, a2: LandingSite): boolean {
    return a1 && a2 ? a1.name == a2.name: a1 === a2;
  }


  compareArea(a1: ManagementArea | Subarea, a2: ManagementArea | Subarea): boolean {
    return a1 && a2 ? a1.name == a2.name && a1.ID === a2.ID : a1 === a2;
  }


  // true when a last management area has been selected (auto-filled or manually chosen)
  isAreaValid(): boolean {
    return !!this.endManagementArea?.name;
  }


  // the latest day (time stripped) among all DFO-confirmed catch/pause reports in the trip,
  // or null if none exist yet. The end date can't fall before this day.
  // Only reports with a "FOS…" confirmation number count — unconfirmed reports (awaiting
  // response, errored, not submitted) can still change or be withdrawn, so they don't gate the end date.
  private getLatestReportDay(): Date | null {
    const isConfirmed = (confirmationNumber: string) =>
      SharedMethodsService.getReportStatus(confirmationNumber) === 'Accepted';
    const reportDates: Date[] = [
      ...this.currTrip.dailyCatchReports.filter(r => isConfirmed(r.confirmationNumber)).map(r => new Date(r.dateFished)),
      ...this.currTrip.pauseFishingReports.filter(r => isConfirmed(r.confirmationNumber)).map(r => new Date(r.pauseDate)),
    ];
    if (!reportDates.length) return null;
    const latest = reportDates.reduce((a, b) => (a.getTime() > b.getTime() ? a : b));
    latest.setHours(0, 0, 0, 0);
    return latest;
  }


  // true when the selected end date is on or after every catch/pause report day
  isEndDateValid(): boolean {
    if (!this.minEndDate) return true;
    const end = new Date(this.endDate);
    end.setHours(0, 0, 0, 0);
    return end.getTime() >= this.minEndDate.getTime();
  }


  // the management area is auto-filled from the last catch/pause report, but the user can override it.
  // A manual change invalidates the derived subarea, so clear it to avoid emitting a mismatched Area-Subarea.
  onManagementAreaChange(area: ManagementArea): void {
    this.endManagementArea = area;
    this.endSubArea = new Subarea(); // derived subarea no longer valid for a manually chosen area
    this.subAreas = this.sharedMethodsService.getSubareas(area.name, false, CommonDataService.subareas); // refresh selectable subareas for the new area
    this.configureArrivalForm(); // area change can toggle whether the arrival form is required (license A/C, areas 3/4/5)
  }


  // check if the trip is missing a report for any of the days in the trip
  // Called  on initialization and any time the end date is changed
  checkTripHasMissingReports(endDate: Date): void {
    // make copies & strip time
    let start = new Date(this.currTrip.startFishingReport.startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    while (start <= end) {
      const hasDaily = this.currTrip.dailyCatchReports.some(r => this.isSameDay(r.dateFished, start));
      const hasPause = this.currTrip.pauseFishingReports.some(r => this.isSameDay(r.pauseDate, start));

      if (!hasDaily && !hasPause) {
        this.tripHasMissingReports = true;
        return;
      }
      // advance to next calendar day
      start.setDate(start.getDate() + 1);
    }

    this.tripHasMissingReports = false;
  }

// helper to test if two Dates fall on the same calendar day
  private isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear()
      && d1.getMonth() === d2.getMonth()
      && d1.getDate() === d2.getDate();
  }


  /* ---------- Page Navigation Methods ---------- */


  // check current page before going to the next one
  toNextPage(): void {

    if (this.currPage > this.numPages) {
      if (!this.endTime) {
        this.sharedMethodsService.toastInvalidForm();
        return;
      }
      if (this.needsArrivalForm) {  // don't allow submission if the arrival form is required and not valid
        if (!this.arrivalForm.get('site')!.valid || !this.plannedOffloadTime || !this.endForm.get('comments')!.valid) {
          this.sharedMethodsService.toastInvalidForm();
          return;
        }
      }
      // page is final page
    if (this.isEditing) {
      this.submitEdits();
    } else {
      this.submitReport();
    }
    return;
    }

    if (this.currPage == 1) {
      if (this.tripHasMissingReports) {
        this.dialog.open(ConfirmationDialogComponent, {
          data: { screen: 'missing_reports' }
        }).afterClosed().subscribe(result => {
          if (result) {
            this.completeFirstPage();
          }
        });
      } else {
        this.completeFirstPage();
      }
      return;
    }

    if (this.currPage == 2 && this.needsArrivalForm) {
      if (!this.arrivalForm.get('site')!.valid || this.arrivalForm.hasError('offloadTooEarly')) {
        this.sharedMethodsService.toastInvalidForm();
      } else {
        if (this.plannedOffloadTime) {
          this.sharedMethodsService.setDateTime(this.plannedOffloadDate, this.plannedOffloadTime); // add planned offload time to the planned offload date
        }
        this.incrementPage();
      }
      return;
    }

    if (this.currPage == 3 && this.needsArrivalForm) {
      if (!this.endForm.get('comments')!.valid) {
        this.sharedMethodsService.toastInvalidForm();
        this.endForm.markAllAsTouched();
      } else {
        this.incrementPage();
      }
        return;
    }

    this.incrementPage();

  }


  // go forward a page, changing the current page number and the spinner progress
  incrementPage(): void {
    this.currPage++;
    this.spinnerProgress += this.spinnerIncrement;
  }


  // check current page before going to the previous one
  // validate the end time on the first page, then move to the next page
  private completeFirstPage(): void {
    if (!this.endTime || !this.isAreaValid()) {
      this.sharedMethodsService.toastInvalidForm();
    } else if (!this.isEndDateValid()) {
      this.sharedMethodsService.toastEndDateBeforeReports(); // end date can't precede a catch/pause report
    } else {
      this.sharedMethodsService.setDateTime(this.endDate, this.endTime); // add end time to the end date
      this.incrementPage();
    }
  }


  toPreviousPage(): void {

    if (this.isComplete || this.currPage === 1) {
      this.sharedMethodsService.toHome(this.userDetails.currHomepage);
    } else {
      this.currPage--;
      this.spinnerProgress -= this.spinnerIncrement;
    }

  }


  
  /* ---------- Report Completion Methods ---------- */


  // returns the current date with the current arrival time, the returned date should only be used to read time
  getArrivalTime(time: any): Date | undefined {

    if (time === undefined || time === null) {
      return;
    }
    let tempDate: Date = new Date();
    this.sharedMethodsService.setDateTime(tempDate, time);
    return tempDate;

  }


  getNewHomepage(): string {
      return 'active-trip-home';        
  }

  submitEdits(): void {

    if (this.onlineStatusService.getStatus() !== OnlineStatusType.ONLINE) {
      this.sharedMethodsService.toastOfflineError();
      return;
    }
    
    if (!this.arrivalForm.valid || !this.endForm.valid) {
      this.sharedMethodsService.toastInvalidForm();
      return;
    }

    if (!this.isEndDateValid()) { // end date can't precede a catch/pause report
      this.sharedMethodsService.toastEndDateBeforeReports();
      return;
    }

    this.isReportSending = true;
    this.saveReportData();

    const changes: FieldChange[] = this.getChangedFields();

    const isActiveTrip = this.currTrip.tripID === UserStorageService.currTrip.tripID;
    UserStorageService.saveTrip(this.currTrip, isActiveTrip, true).then(() => {
        // send an email to archipelago with the fisher's info, including the changed fields
        const payload = {
          fisherName: this.userDetails.firstName + ' ' + this.userDetails.lastName,
          fin: this.currTrip.fin,
          vesselName: this.currTrip.vessel.vesselName,
          vrn: this.currTrip.vessel.vesselRegistrationNumber,
          logbook: this.currTrip.logbook.logbookID,
          tripId: this.currTrip.tripID,
          reportId: this.endFishingReport.reportID,
          changedFields: changes,
        };

        this.emailService.sendEditNotification(payload).subscribe(
          () => console.log("Edit‐notification email queued"),
          err => console.error("Failed to queue edit email", err)
        );
        this.router.navigate(['submission-page'], {state: { isEditSubmission: true }});
      });
  }


  // every field of the report a fisher can reach while editing, compared against the submitted version
  private getChangedFields(): FieldChange[] {

    const changed: FieldChange[] = [];
    const original: EndReport = this.originalEndReport;
    const updated: EndReport = this.endFishingReport;

    ReportDiffService.add(changed, 'endDate', 'End Date', original.endDate, updated.endDate, ReportDiffService.dateTime);
    ReportDiffService.add(changed, 'managementArea', 'Last Management Area', original.managementArea, updated.managementArea);
    ReportDiffService.add(changed, 'subArea', 'Last Subarea Fished', original.subArea, updated.subArea);
    ReportDiffService.add(changed, 'landingSite', 'Planned Landing Site', original.landingSite, updated.landingSite);
    // the arrival details only reach DFO when the trip needs them, so only report them then
    if (this.needsArrivalForm) {
      ReportDiffService.add(changed, 'plannedOffloadDate', 'Planned Offload Date', original.plannedOffloadDate, updated.plannedOffloadDate, ReportDiffService.dateTime);
    }
    ReportDiffService.add(changed, 'comments', 'Comments', original.comments, updated.comments);

    return changed;

  }


  // verifies that all required report fields are complete, then initiates the saving/sending process
  submitReport(): void {

    if (!this.isAreaValid()) { // never let an end report go to DFO without a last management area
      this.sharedMethodsService.toastInvalidForm();
      return;
    }

    if (!this.isEndDateValid()) { // never let an end date precede a catch/pause report
      this.sharedMethodsService.toastEndDateBeforeReports();
      return;
    }

    if (this.arrivalForm.valid && this.endForm.valid) {
      this.isReportSending = true;
      this.saveReportData(); // save the report data and submit if the report/form is valid
      this.attemptTransmission(); // attempt to send the report to DFO
    } else { // otherwise, display an error message and do not submit
      this.sharedMethodsService.toastInvalidForm();
    }

  }


  // save End Fishing Report and Trip Data
  saveReportData(): void {

    this.endFishingReport.landingSite = LandingSite.transformAnonymous(this.arrivalForm.get('site')?.value);
    if (this.needsArrivalForm) {
      this.sharedMethodsService.setDateTime(this.plannedOffloadDate, this.plannedOffloadTime); // add planned offload time to the planned offload date
      this.endFishingReport.plannedOffloadDate = this.plannedOffloadDate;
    }
    this.sharedMethodsService.setDateTime(this.endDate, this.endTime); // add end time to the end date
    this.endFishingReport.endDate = this.endDate; // save end date to report
    this.endFishingReport.managementArea = this.endManagementArea; // save management area to the report
    this.endFishingReport.subArea = this.endSubArea;
    this.endFishingReport.comments =  this.endForm.get('comments')?.value.trim();
    this.endFishingReport.isComplete = true; // set submission flag
    this.endFishingReport.completionDate = new Date(); // get current date/time
    this.endFishingReport.submissionDate = new Date();
    this.currTrip.endFishingReport = this.endFishingReport;

    this.userDetails.currHomepage = this.getNewHomepage(); // update the user's homepage

  }


   // composes the email body and subject, then invokes a method to email the report to DFO
   attemptTransmission(): void {
    // A reopened report still carries the old FOS error text; clear it before the pre-send save,
    // otherwise the saveTrip merge sees matching completionDates and restores the stale error
    // over the resubmission (ties keep the error).
    if (SharedMethodsService.getReportStatus(this.endFishingReport.confirmationNumber) === 'Error with submission')
      this.endFishingReport.confirmationNumber = 'Awaiting response';
    UserStorageService.saveUser(this.userDetails).then(() => {
      UserStorageService.saveTrip(this.currTrip).then(() => {
        // change false to !isProduction to enable mocking the submission
        if (false) { // mock the submission process if the app is in development mode
          this.sharedMethodsService.toastEmailSent();
          this.endFishingReport.confirmationNumber = "FOS 12345";
          this.endFishingReport.isSubmitted = true;
          UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'End Fishing Report', success: this.endFishingReport.isSubmitted }, replaceUrl: true }));
        } else {
          this.submissionService.sendReportData(this.userDetails.userEmail, this.endFishingReport.reportID, this.currTrip.tripID).then((success: boolean) => {
            if (success) { // if the email was sent successfully
              this.sharedMethodsService.toastEmailSent();
              this.endFishingReport.isSubmitted = true; // set the report submissionflag
              this.endFishingReport.confirmationNumber = "Awaiting response";
              // replaceUrl so browser back from the submission page can't land on this form again
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'End Fishing Report', success: this.endFishingReport.isSubmitted }, replaceUrl: true }));
            } else {
              this.endFishingReport.confirmationNumber = "Not submitted";
              this.sharedMethodsService.toastUnableToSubmitReport();
              this.currTrip.unsubmittedReports.push(this.endFishingReport.completionDate);
              UserStorageService.saveTrip(this.currTrip).then(() => this.router.navigate(['submission-page'], { state: { name: 'End Fishing Report', success: this.endFishingReport.isSubmitted }, replaceUrl: true }));
            }
          });
        }
      });
    });   

  }


  // Custom validator function
  updateCommentsValidator(siteValue: string) {
    const commentsControl = this.endForm.get('comments');
    this.nonListedSite = siteValue == 'Not Listed';
    if (siteValue == 'Not Listed') commentsControl?.setValidators([Validators.required]);
    else commentsControl?.clearValidators();
    
    commentsControl?.updateValueAndValidity();
  }

  private offloadAfterEndValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
    
      // if the arrival form is not needed, don't block
      if (!this.needsArrivalForm) return null;

      // if either date/time is missing, don’t block
      if (!this.plannedOffloadDate || !this.plannedOffloadTime || !this.endDate || !this.endTime) {
        return null;
      }

      const planned = new Date(this.plannedOffloadDate);
      planned.setHours(this.plannedOffloadTime.hour, this.plannedOffloadTime.minute);

      const end = new Date(this.endDate);
      end.setHours(this.endTime.hour, this.endTime.minute);

      // if planned is not strictly after end, set an error
      return planned.toISOString() > end.toISOString() ? null : { offloadTooEarly: true };
    };
  }
  
  onEndDateChange(newDate: Date) {
    this.endDate = newDate;
    this.checkTripHasMissingReports(this.endDate);
    // let Angular finish its current cycle before re‑validating
    setTimeout(() => this.arrivalForm.updateValueAndValidity(), 0);
  }

  onEndTimeChange(newTime: { hour: number; minute: number }) {
    this.endTime = newTime;
    setTimeout(() => this.arrivalForm.updateValueAndValidity(), 0);
  }

  onPlannedOffloadDateChange(newDate: Date) {
    this.plannedOffloadDate = newDate;
    setTimeout(() => this.arrivalForm.updateValueAndValidity(), 0);
  }

  onPlannedOffloadTimeChange(newTime: { hour: number; minute: number }) {
    this.plannedOffloadTime = newTime;
    setTimeout(() => this.arrivalForm.updateValueAndValidity(), 0);
  }


}
